'use client';

/**
 * PWAInstallPrompt — shows a native-style "Add to Home Screen" banner.
 *
 * Captures the `beforeinstallprompt` event (Chrome/Edge/Android) before it fires
 * and defers showing it until the user clicks our own CTA. Falls back to hidden
 * on iOS/Safari (where the event doesn't exist).
 *
 * Dismissed state is persisted in localStorage so the banner doesn't reappear
 * after the user explicitly closes it.
 */

import { useEffect, useState } from 'react';
import { Download, X } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISSED_KEY = 'favely:pwa-prompt-dismissed';

export function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [dismissed, setDismissed] = useState(true); // start hidden to avoid flash

  useEffect(() => {
    // Don't show if user already dismissed
    if (localStorage.getItem(DISMISSED_KEY)) return;

    // Don't show if already installed (display-mode: standalone)
    if (window.matchMedia('(display-mode: standalone)').matches) return;

    function handleInstallPrompt(e: Event) {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setDismissed(false);
    }

    window.addEventListener('beforeinstallprompt', handleInstallPrompt);
    return () => window.removeEventListener('beforeinstallprompt', handleInstallPrompt);
  }, []);

  async function handleInstall() {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted' || outcome === 'dismissed') {
      handleDismiss();
    }
  }

  function handleDismiss() {
    localStorage.setItem(DISMISSED_KEY, '1');
    setDismissed(true);
    setDeferredPrompt(null);
  }

  if (dismissed || !deferredPrompt) return null;

  return (
    <div
      role="banner"
      className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-sm"
    >
      <div className="flex items-center gap-3 rounded-none border-2 border-border bg-card px-4 py-3 shadow-[4px_4px_0_var(--border)]">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold leading-tight">Add Favely to your home screen</p>
          <p className="text-xs text-muted-foreground mt-0.5">Works offline, loads instantly.</p>
        </div>
        <button
          onClick={handleInstall}
          className="shrink-0 flex items-center gap-1.5 rounded-none border-2 border-primary bg-primary px-3 py-1.5 text-xs font-bold text-primary-foreground shadow-[2px_2px_0_oklch(0.52_0.2_12)] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[1px_1px_0_oklch(0.52_0.2_12)] transition-all"
        >
          <Download className="h-3.5 w-3.5" />
          Install
        </button>
        <button
          onClick={handleDismiss}
          aria-label="Dismiss install prompt"
          className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
